import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listBookings } from '@/services/bookings.service';
import { useBookingAlarmStore } from '@/store/bookingAlarm.store';
import { useClubStore } from '@/store/club.store';
import { qk } from './queryKeys';

/**
 * Watches for new bookings made from the public site and surfaces the ones this
 * device hasn't acknowledged yet. Polls every 15s (works in both data providers,
 * no realtime setup needed). Existing bookings are baselined on first load so
 * only genuinely new ones raise the alarm.
 */
export function useBookingAlarm() {
  const clubId = useClubStore((s) => s.activeClubId);
  const { data: bookings = [], isSuccess } = useQuery({
    queryKey: qk.bookings(clubId),
    queryFn: () => listBookings(clubId),
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
  });

  const initialized = useBookingAlarmStore((s) => s.initialized);
  const seenIds = useBookingAlarmStore((s) => s.seenIds);
  const markSeen = useBookingAlarmStore((s) => s.markSeen);

  // Public bookings = came from the public site (no operator) and still pending.
  const publicBookings = useMemo(
    () => bookings.filter((b) => b.createdBy == null && b.status === 'CONFIRMED'),
    [bookings],
  );

  // Baseline existing bookings once, after the first successful fetch.
  const baselined = useRef(false);
  useEffect(() => {
    if (!isSuccess || initialized || baselined.current) return;
    baselined.current = true;
    markSeen(publicBookings.map((b) => b.id));
  }, [isSuccess, initialized, publicBookings, markSeen]);

  const seen = useMemo(() => new Set(seenIds), [seenIds]);
  const pending = useMemo(
    () => (initialized ? publicBookings.filter((b) => !seen.has(b.id)) : []),
    [initialized, publicBookings, seen],
  );

  const acknowledge = () => markSeen(publicBookings.map((b) => b.id));

  return { pending, acknowledge };
}
